import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useChainId } from 'wagmi';
import { useAddLiquidityModal } from '@/components/AddLiquidity/AddLiquidityModalProvider';
import { getPool, getPools, TOKEN_CONTRACTS } from '@/onchain/pools';
import { PoolDetailsData } from './usePoolDetailsData';

/**
 * Shared hook for Explore pool detail action buttons (Swap + Create Position).
 * Resolves real on-chain pool/token data from mock pool symbols and builds
 * navigation handlers for both desktop header and mobile footer.
 */
export function useExplorePoolActions(pool: PoolDetailsData) {
  const router = useRouter();
  const chainId = useChainId();
  const { openAddLiquidityModal } = useAddLiquidityModal();

  const currencyA = TOKEN_CONTRACTS[pool.baseSymbol]?.[chainId] as `0x${string}` | undefined;
  const currencyB = TOKEN_CONTRACTS[pool.quoteSymbol]?.[chainId] as `0x${string}` | undefined;

  const resolvedPool = useMemo(() => {
    const symbolMatch = getPool(chainId, pool.baseSymbol, pool.quoteSymbol);
    if (symbolMatch) return symbolMatch;

    if (!currencyA || !currencyB) return undefined;

    // When symbol lookup fails, try matching by resolved currency addresses
    // against pool keys to maximise the chance of passing a poolId.
    const sorted = [currencyA.toLowerCase(), currencyB.toLowerCase()].sort();
    return getPools(chainId)?.find((p) => {
      const keySorted = [p.key.currency0.toLowerCase(), p.key.currency1.toLowerCase()].sort();
      return keySorted[0] === sorted[0] && keySorted[1] === sorted[1];
    });
  }, [chainId, pool.baseSymbol, pool.quoteSymbol, currencyA, currencyB]);

  const handleSwapClick = useCallback(() => {
    if (currencyA && currencyB) {
      void router.push(`/swap?currencyA=${currencyA}&currencyB=${currencyB}`);
    } else {
      void router.push('/swap');
    }
  }, [currencyA, currencyB, router]);

  const handleCreatePositionClick = useCallback(() => {
    if (resolvedPool) {
      openAddLiquidityModal({ poolId: resolvedPool.keyBytes32 });
    } else if (currencyA || currencyB) {
      openAddLiquidityModal({ currencyFilters: { currencyA, currencyB } });
    } else {
      openAddLiquidityModal();
    }
  }, [openAddLiquidityModal, resolvedPool, currencyA, currencyB]);

  return { handleSwapClick, handleCreatePositionClick };
}
